(() => {
  if (customElements.get('product-new-bundle-button')) return;

  class ProductNewBundleButton extends HTMLElement {
    constructor() {
      super();
      this.onAddToCart = this.onAddToCart.bind(this);
      this.onPurchaseTypeChange = this.onPurchaseTypeChange.bind(this);
    }

    connectedCallback() {
      this.setEventListeners();
      this.renderPrices();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    setEventListeners() {
      this.querySelector('.js-product-new-bundle-add')?.addEventListener('click', this.onAddToCart);
      document.addEventListener('change', this.onPurchaseTypeChange);
    }

    removeEventListeners() {
      this.querySelector('.js-product-new-bundle-add')?.removeEventListener('click', this.onAddToCart);
      document.removeEventListener('change', this.onPurchaseTypeChange);
    }

    onPurchaseTypeChange(event) {
      if (event.target.name !== 'purchase_type') return;
      this.renderPrices();
    }

    renderPrices() {
      const isSubscription = this.isSubscriptionActive();

      const tierPrice = parseInt(this.dataset.basePrice, 10) || 0;
      const tierComparePrice = parseInt(this.dataset.baseComparePrice, 10) || 0;
      const compareRef = parseInt(this.dataset.compareRef, 10) || 0;

      const salePrice = isSubscription ? tierPrice : tierComparePrice;
      const comparePriceValue = compareRef || tierComparePrice;

      const priceEl = this.querySelector('.js-bundle-price');
      const comparePriceEl = this.querySelector('.js-bundle-compare-price');

      if (priceEl) priceEl.textContent = this.formatMoney(salePrice);

      if (comparePriceEl) {
        if (comparePriceValue > salePrice) {
          comparePriceEl.textContent = this.formatMoney(comparePriceValue);
          comparePriceEl.classList.remove('is-hidden');
        } else {
          comparePriceEl.classList.add('is-hidden');
        }
      }
    }

    formatMoney(cents) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
        minimumFractionDigits: 2,
      });
      return formatter.format(cents / 100);
    }

    isSubscriptionActive() {
      const purchaseTypeInput = document.querySelector(
        '.js-product-new-form-purchase-type[value="subscribe"]:checked'
      );
      if (purchaseTypeInput) return true;
      const scheduleSelect = document.querySelector('.js-product-new-form-schedule');
      return scheduleSelect ? !scheduleSelect.disabled : false;
    }

    getActiveDiscount() {
      const subscriptionDiscount = parseInt(this.dataset.subscriptionDiscount, 10) || 0;
      const regularDiscount = parseInt(this.dataset.discount, 10) || 0;
      if (subscriptionDiscount > 0 && this.isSubscriptionActive()) {
        return subscriptionDiscount;
      }
      return regularDiscount;
    }

    async onAddToCart() {
      const button = this.querySelector('.js-product-new-bundle-add');
      const variantId = this.dataset.variantId;
      const quantity = parseInt(this.dataset.quantity, 10) || 1;

      if (!variantId || button?.disabled) return;

      const errorEl = this.querySelector('.js-bundle-error');
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.hidden = true;
      }

      button.disabled = true;
      button.classList.add('is-loading');

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);

        const pdpForm = document.querySelector('product-new-form form');
        const scheduleSelect = pdpForm?.querySelector('.js-product-new-form-schedule');
        const sellingPlanInput = pdpForm?.querySelector('[name="selling_plan"]');
        const isSubscription = scheduleSelect && !scheduleSelect.disabled;
        if (isSubscription && sellingPlanInput?.value) {
          formData.append('selling_plan', sellingPlanInput.value);
        }

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        if (cart) {
          formData.append('sections', cart.getSectionsToRender().map((s) => s.id).join(','));
          formData.append('sections_url', window.location.pathname);
        }

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.description);
        }

        const data = await response.json();

        const productNew = this.closest("product-new");
        if (productNew?.hasAttribute("data-redirect-checkout")) {
          const checkoutUrl =
            typeof routes !== "undefined" && routes.root
              ? `${routes.root}checkout`
              : "/checkout";
          window.location.href = checkoutUrl;
          return;
        }

        if (cart) {
          if (typeof cart.open === 'function') cart.open();
          if (typeof cart.renderContents === 'function') cart.renderContents(data);
        }

        window.dispatchEvent(new CustomEvent('cart:add', { detail: { data } }));
      } catch (error) {
        console.error('Bundle add to cart error:', error);
        if (errorEl) {
          errorEl.textContent = error.message || window.cartStrings?.error || '';
          errorEl.hidden = false;
        }
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }
  }

  customElements.define('product-new-bundle-button', ProductNewBundleButton);
})();

(() => {
  if (customElements.get('product-new-bundles')) return;

  class ProductNewBundles extends HTMLElement {
    constructor() {
      super();
      this.onPdpVariantChange = this.onPdpVariantChange.bind(this);
    }

    connectedCallback() {
      window.addEventListener('pdp:variant:change', this.onPdpVariantChange);
    }

    disconnectedCallback() {
      window.removeEventListener('pdp:variant:change', this.onPdpVariantChange);
    }

    async onPdpVariantChange(event) {
      const { variantId } = event.detail;
      if (!variantId) return;

      const sectionId = this.closest('[data-section-id]')?.dataset.sectionId;
      if (!sectionId) return;

      try {
        const url = `${window.location.pathname}?variant=${variantId}&section_id=${sectionId}`;
        const response = await fetch(url);
        if (!response.ok) return;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newList = doc.querySelector('.product-new-bundles__list');
        const currentList = this.querySelector('.product-new-bundles__list');

        if (newList && currentList) {
          currentList.replaceWith(newList);
          console.log('[product-new-bundles] list replaced');
        }
      } catch (error) {
        console.error('[product-new-bundles] Bundle list refresh error:', error);
      }
    }
  }

  customElements.define('product-new-bundles', ProductNewBundles);
})();
